import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  broadcastReceiver,
} from '@hh/global.js'

class HHDoubleDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    secondDrawerOpen: {
      type: Boolean,
      attribute: 'second-drawer-open',
      reflect: true,
    },
    side: {
      type: String,
      reflect: true,
    },
    labelledBy: {
      type: String,
      attribute: 'labelledby',
    },
    describedBy: {
      type: String,
      attribute: 'describedby',
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    // Display exit intent popup when trying to close the drawer
    confirmClose: {
      type: Boolean,
      default: false,
      attribute: 'confirm-close'
    },
    confirmPopupId: {
      type: String,
      attribute: 'confirm-popup-id'
    }
  }

  constructor() {
    super()

    this.isReducedMotion = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (!this.side) {
      this.side = 'right'
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('drawer', (...args) => {
      this.openFromDocument(...args)
    })

    window.addEventListener('keydown', this.onEscKey.bind(this))
    window.addEventListener(
      'double-drawer:close',
      this.closeFromEvent.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()

    window.removeEventListener('keydown', this.onEscKey)
    window.removeEventListener('double-drawer:close', this.closeFromEvent)
  }

  get drawer() {
    return this.shadowRoot.querySelector('.drawer') ?? null
  }

  openDrawer() {
    this.open = true
  }

  closeDrawer(dispatch = false, force = false) {
    if (this.confirmClose && !force) {
      document.body.setAttribute('modal', JSON.stringify({
        id: this.confirmPopupId
      }))

      return
    }

    this.open = false
    this.secondDrawerOpen = false
    if (dispatch) {
      this.dispatchEvent(
        new CustomEvent('drawer:close', { bubbles: true })
      )
    }
    if (this.openerSelector) {
      const opener = document.querySelector(this.openerSelector)
      opener?.focus({
        preventScroll: true,
        focusVisible: false,
      })
    }
  }

  // Handlers
  openFromDocument(data) {
    if (this.id === data.id) {
      this.openDrawer()
    }
  }

  closeFromDrawer(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.closeDrawer()
    }
  }

  closeFromEvent() {
    this.closeDrawer(false, true)
  }

  onCloseClick() {
    this.closeDrawer(true)
  }

  onEscKey(e) {
    if (e.key === 'Escape' && this.open) {
      this.closeDrawer()
    }
  }

  onTransitionEnd() {
    if (!this.open) return
    this.secondDrawerOpen = true
  }

  render() {
    return html`
      <div
        class=${classMap({
      overlay: true,
      open: this.open,
      'reduced-motion': this.isReducedMotion,
    })}
        @click=${this.onCloseClick}
        aria-hidden="true"
      ></div>
      <div
        class=${classMap({
      drawer: true,
      open: this.open,
      'reduced-motion': this.isReducedMotion,
    })}
        role="dialog"
        aria-modal="true"
        aria-labelledby=${ifDefined(this.labelledBy)}
        aria-describedby=${ifDefined(this.describedBy)}
        ?inert=${!this.open}
        @transitionend=${this.onTransitionEnd}
        @click=${this.closeFromDrawer}
      >
        <hh-focus-trap active="${ifDefined(this.open)}">
          <slot name="close" @click=${this.onCloseClick}></slot>
          <slot></slot>
        </hh-focus-trap>
      </div>
      <div
        class=${classMap({
      'second-drawer': true,
      open: this.secondDrawerOpen,
      'reduced-motion': this.isReducedMotion,
    })}
        @click=${this.closeFromDrawer}
      >
        <slot name="second-drawer"></slot>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .overlay {
      position: fixed;
      inset: 0;
      z-index: 109;
      background-color: var(--hh-overlay-color, rgba(0, 0, 0, 0.5));
      backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
      -webkit-backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--hh-duration-default) ease-in-out;
      min-width: 100%;
      min-height: 100%;
      width: 100vw;
      height: 100vh;
    }

    .overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .drawer,
    .second-drawer {
      position: fixed;
      top: 0;
      z-index: 111;
      height: 100%;
      width: 100%;
      max-width: var(--hh-max-width, 65rem);
      max-height: 100vh;
      background-color: var(--hh-background-color, var(--hh-color-day, #fff));
      overflow-y: auto;
      transition: transform var(--hh-duration-long) var(--hh-cubic-1);
    }

    .second-drawer {
      max-width: calc(100% - var(--hh-max-width, 65rem) - 4rem);
      z-index: 110;
    }

    :host([side='left']) .drawer,
    :host([side='left']) .second-drawer {
      right: auto;
      left: 0;
      transform: translate3d(-100%, 0, 0);
    }

    :host([side='right']) .drawer,
    :host([side='right']) .second-drawer {
      right: 0;
      left: auto;
      transform: translate3d(100%, 0, 0);
    }

    @media (min-width: 990px) {
      :host([side='left']) .drawer {
        border-right: 1px solid var(--hh-border-color, var(--hh-color-neutral));
      }

      :host([side='right']) .drawer {
        border-left: 1px solid var(--hh-border-color, var(--hh-color-neutral));
      }
    }

    :host .drawer.open {
      transform: translate3d(0, 0, 0);
    }

    :host([side='left']) .second-drawer.open {
      transform: translate3d(var(--hh-max-width), 0, 0);
    }

    :host([side='right']) .second-drawer.open {
      transform: translate3d(calc(var(--hh-max-width) * -1), 0, 0);
    }

    ::slotted([slot='close']) {
      position: absolute;
      cursor: pointer;
      top: var(--hh-close-top, 2.1rem);
      right: var(--hh-close-right, 1.8rem);
      color: var(--hh-close-color, inherit);
      background-color: var(--hh-close-background-color, transparent);
      border: var(--hh-close-border, none);
      padding: var(--hh-close-padding, 0.4rem 1rem);
      border-radius: var(--hh-close-border-radius, 3.5rem);
      font: var(--hh-close-font, inherit);
      transition: transform 0.7s ease;
      z-index: 5;
    }

    ::slotted([slot='close']:hover) {
      transform: scale(1.2);
    }

    .drawer.reduced-motion,
    .overlay.reduced-motion {
      transition: none;
    }
  `
}

customElements.define('hh-double-drawer', HHDoubleDrawer)
